// WelcomeBannerV3 — hero banner at the top of DashboardV3.
//
// Mode is driven by the `bannerMode` prop:
//   - "new"       → gradient banner + 3-step onboarding stepper + skip CTA
//   - "returning" → compact bentornato banner (no stepper)
//
// Steps come from /api/dashboard-v3/onboarding/status; skip + complete-step
// calls flow back through the parent so the banner can react optimistically.
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import OnboardingStepV3 from "../components/OnboardingStepV3";

export default function WelcomeBannerV3({
    bannerMode,
    firstName,
    steps,
    onSkip,
    skipping,
}) {
    const { t } = useTranslation("dashboard_v3");

    const isNew = bannerMode === "new";
    const headline = isNew
        ? t("welcome.headline_new", { name: firstName })
        : t("welcome.headline_returning", { name: firstName });
    const sub = isNew ? t("welcome.sub_new") : t("welcome.sub_returning");

    const stepDefs = [
        {
            key: "email",
            title: t("onboarding.step_email_title"),
            subtitle: t("onboarding.step_email_sub"),
            href: null,
        },
        {
            key: "target",
            title: t("onboarding.step_target_title"),
            subtitle: t("onboarding.step_target_sub"),
            href: "/leads",
        },
        {
            key: "campaign",
            title: t("onboarding.step_campaign_title"),
            subtitle: t("onboarding.step_campaign_sub"),
            href: "/outreach/campaigns/new",
        },
    ];

    const activeIdx = stepDefs.findIndex((s) => !steps?.[s.key]);

    return (
        <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="dashboard-v3-welcome"
            data-mode={bannerMode}
            className="relative overflow-hidden rounded-2xl border p-6 lg:p-8"
            style={{
                background: "linear-gradient(135deg, #111111 0%, #1A1A1A 100%)",
                borderColor: "#1A1A1A",
            }}
        >
            {/* Ambient blue overlay */}
            <div
                aria-hidden
                className="absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl opacity-25"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(255, 255, 255,0.6), transparent 70%)",
                }}
            />

            <div className="relative">
                <p className="text-[11px] uppercase tracking-widest text-[#CCCCCC]">
                    Dashboard
                </p>
                <h1 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-[#FFFFFF]">
                    {headline}
                </h1>
                <p className="mt-2 text-sm text-[#CCCCCC] max-w-2xl">{sub}</p>

                {isNew && (
                    <>
                        <ol
                            className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-2"
                            aria-label="Onboarding stepper"
                        >
                            {stepDefs.map((s, idx) => (
                                <OnboardingStepV3
                                    key={s.key}
                                    idx={idx}
                                    testId={`dashboard-v3-onboarding-step-${s.key}`}
                                    completed={!!steps?.[s.key]}
                                    active={idx === activeIdx}
                                    title={s.title}
                                    subtitle={s.subtitle}
                                    href={s.href}
                                />
                            ))}
                        </ol>

                        <div className="mt-4">
                            <button
                                type="button"
                                onClick={onSkip}
                                disabled={skipping}
                                data-testid="dashboard-v3-onboarding-skip"
                                className="text-xs text-[#CCCCCC] hover:text-white transition-colors underline-offset-4 hover:underline disabled:opacity-60"
                            >
                                {t("welcome.skip_button")}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </motion.section>
    );
}
